import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from '../config';
import { cartLabels } from '../utils/carts';
import CargaBeta from './CargaBeta';
import { loadSavedLoad, pruneSavedLoads } from '../utils/cargaBetaStorage';
import "../styles/productos.css";

function Productos() {
  const { ponumber } = useParams();
  const { token } = useAuth();
  const [poData, setPoData] = useState(null);
  const [searchSku, setSearchSku] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  // A load saved on this device reopens the beta right away. Old saves are
  // dropped first so a forgotten load doesn't come back after days.
  const [showBetaMode, setShowBetaMode] = useState(() => {
    pruneSavedLoads();
    return loadSavedLoad(ponumber) !== null;
  });
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const fetchPoData = async () => {
      // Without the order from the server, a load saved on this device goes
      // on with the order it saved.
      const fallBackToSavedOrder = () => {
        const saved = loadSavedLoad(ponumber);
        if (saved && saved.order) {
          setPoData(saved.order);
          setOffline(true);
        } else {
          setPoData(null);
        }
      };
      // First try to use order data from navigation state
      if (location.state?.orderData) {
        setPoData(location.state.orderData);
        return;
      }

      // If no order data in state, fetch from get-po endpoint
      if (!token) {
        console.error("No token available");
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/get-po/?po_number=${ponumber}`, {
          method: "GET",
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPoData(data);
        } else {
          console.error("Failed to fetch PO data", response.status, response.statusText);
          fallBackToSavedOrder();
        }
      } catch (error) {
        console.error("Error:", error);
        fallBackToSavedOrder();
      }
    };

    fetchPoData();
  }, [token, ponumber, location.state]);

  const filteredProducts = poData?.items?.filter((item) =>
    item.sku_code.toString().includes(searchSku)
  ) || [];

  const handleBack = () => {
    navigate(`/admin`);
  };

  const handleCheckOrder = () => {
    // Pass the order data and callback to the next component
    navigate(`/paid/${ponumber}/productos/check`, {
      state: {
        orderData: poData,
        onOrderChecked: location.state?.onOrderChecked
      }
    });
  };

  const handleBetaMode = () => {
    setShowBetaMode(true);
  };

  const handleContinueBeta = (scannedItems) => {
    navigate(`/paid/${ponumber}/productos/check`, {
      state: { orderData: poData, betaScannedItems: scannedItems, betaCompleted: true },
    });
  };

  if (showBetaMode) {
    if (!poData) {
      return <div className="productos-container"><p>Cargando...</p></div>;
    }
    return (
      <CargaBeta
        ponumber={ponumber}
        order={poData}
        offline={offline}
        onClose={() => setShowBetaMode(false)}
        onContinue={handleContinueBeta}
      />
    );
  }

  return (
    <div className="productos-container">
      <button onClick={handleBack} className="productos-back-button">
        &#8592;
      </button>
      <h1>Productos para el Pedido {ponumber}</h1>

      {/* Beta Button - Hidden for production */}
      <div className="beta-button-container">
        <button onClick={handleBetaMode} className="beta-button">
          Beta
        </button>
      </div>

      {/* Sección de Carts, Responsables y Cliente */}
      {poData && (
        <div className="carts-responsables-section">
          <h3 className="section-title">Buscar:</h3>
          <ul className="cart-list" aria-label="Carros">
            {cartLabels(poData.carts).map((cart) => (
              <li key={cart.code} className="cart-item">
                <strong>{cart.kind}</strong> {cart.number}
              </li>
            ))}
          </ul>

          <h3 className="section-title">Responsables:</h3>
          <ul className="responsables-list">
            {poData.item_responsable?.length > 0 && (
              <li className="responsable-item">
                <strong>Abarrote:</strong>{" "}
                {poData.item_responsable.map(responsable => responsable.name).join(", ")}
              </li>
            )}
            {poData.frozen_responsable?.length > 0 && (
              <li className="responsable-item">
                <strong>Frio:</strong>{" "}
                {poData.frozen_responsable.map(responsable => responsable.name).join(", ")}
              </li>
            )}
          </ul>

          {/* Nueva Sección: Cliente */}
          <h3 className="section-title">Cliente:</h3>
          <p className="client-name"><strong>Nombre:</strong> {poData.client}</p>
        </div>
      )}

      {/* Input de búsqueda por SKU */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Buscar por SKU..."
          value={searchSku}
          onChange={(e) => setSearchSku(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Listado de Productos */}
      <div className="productos-lista">
        <div className="admin-productos-container">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((item, index) => (
              <label key={index} className="product-item" htmlFor={`checkbox-${index}`}>
                <div className="product-checkbox-container">
                  <input type="checkbox" className="product-checkbox" id={`checkbox-${index}`} />
                </div>
                <div className="item-details-productos">
                  <span className="sku-name-productos">{item.sku_name}</span>
                  <span className="item-quantity-label-productos">
                    Cantidad por {item.format_name}:
                  </span>
                  <span className="item-format-quantity-productos">
                    {item.format_quantity} {item.format_unit_kg ? "kg" : ""}
                  </span>
                  <span className="item-sku-code-productos">SKU {item.sku_code}</span>
                </div>

                <div className="quantity-format-container-productos">
                  <div className="quantity-format-container-2-productos">
                    <div className="ordered-quantity-box-productos">
                      <span className="ordered-quantity-productos">
                        {item.format_unit_kg ? `${item.actual_quantity} kg` : item.ordered_quantity}
                      </span>
                    </div>
                    <span className="format-name-productos">{item.format_name}</span>
                  </div>
                  <div className={`status-indicator-productos ${item.status}`}></div>
                </div>
              </label>
            ))
          ) : (
            <div>No hay productos disponibles que coincidan con los criterios.</div>
          )}
        </div>
      </div>

      {/* Botón para chequear el pedido */}
      <div className="check-order-container">
        <button onClick={handleCheckOrder} className="check-order-button">
          Chequear Pedido
        </button>
      </div>
    </div>
  );
}

export default Productos;
